import React, { useEffect } from 'react';
import { pageTitle } from '../../helper';
import Div from '../Div';
import PageHeading from '../PageHeading';
import Spacing from '../Spacing';
import './legal.scss';

export default function PrivacyPolicyPage() {
  pageTitle('Privacy Policy');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <PageHeading
        title="Privacy Policy"
        bgSrc="/images/about_hero_bg.jpeg"
        pageLinkText="Privacy Policy"
      />
      <Spacing lg="100" md="60" />
      <Div className="container">
        <Div className="row">
          <Div className="col-lg-10 offset-lg-1">
            <Div className="cs-legal">
              <p className="cs-legal_meta">Effective Date: August 5, 2026</p>
              <p>
                Acsytech (&quot;Acsytech,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) respects your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard information when you visit acsytech.com (the &quot;Site&quot;) or use our AI automation, web design, and digital marketing services (the &quot;Services&quot;). By using the Site or Services, you agree to the practices described in this policy.
              </p>

              <h2>1. Information We Collect</h2>
              <p>We may collect the following categories of information:</p>
              <ul>
                <li>Contact information you provide, such as name, email address, phone number, and company name (e.g., through contact forms, consultation requests, or account sign-up).</li>
                <li>Business information relevant to providing our Services, such as marketing content, brand assets, or data you share with us to build automation workflows.</li>
                <li>Usage data, such as pages visited, time spent on the Site, referring URLs, browser type, and device information, collected automatically through cookies and similar technologies.</li>
                <li>Payment information, where applicable, which is processed by third-party payment processors and not stored directly by us unless otherwise stated.</li>
                <li>Communications, such as records of correspondence when you contact us via email, forms, or chat.</li>
              </ul>

              <h2>2. How We Use Information</h2>
              <p>We use the information we collect to:</p>
              <ul>
                <li>Provide, operate, and maintain the Site and Services.</li>
                <li>Respond to inquiries, provide quotes, and deliver client projects.</li>
                <li>Improve our Site, Services, and marketing effectiveness.</li>
                <li>Send administrative communications, service updates, and, where you have consented, marketing communications.</li>
                <li>Detect, prevent, and address technical issues, fraud, or misuse.</li>
                <li>Comply with legal obligations.</li>
              </ul>

              <h2>3. Cookies and Tracking Technologies</h2>
              <p>
                We use cookies, pixels, and similar tracking technologies to operate the Site, remember preferences, analyze traffic, and support advertising efforts, which may include tools such as Google Analytics or Meta Pixel. You can control cookies through your browser settings; disabling cookies may affect Site functionality.
              </p>

              <h2>4. How We Share Information</h2>
              <p>
                We do not sell your personal information for monetary consideration. We may share information with:
              </p>
              <ul>
                <li>Service providers who perform functions on our behalf, such as hosting, analytics, email delivery, CRM, and payment processing.</li>
                <li>Professional advisors, such as legal or accounting consultants, where necessary.</li>
                <li>Law enforcement or regulators, where required by law or to protect our rights, property, or safety, or that of others.</li>
                <li>A successor entity in the event of a merger, acquisition, or sale of business assets.</li>
              </ul>
              <p>
                Some third-party analytics or advertising tools may be considered a &quot;sale&quot; or &quot;share&quot; of personal information under California law due to how data is transmitted for advertising purposes; see Section 6 for your opt-out rights.
              </p>

              <h2>5. Data Retention and Security</h2>
              <p>
                We retain personal information for as long as necessary to fulfill the purposes described in this policy, comply with legal obligations, resolve disputes, and enforce agreements. We implement reasonable administrative, technical, and physical safeguards designed to protect information from unauthorized access, loss, or misuse; however, no method of transmission or storage is completely secure.
              </p>

              <h2>6. Your Privacy Rights (California Residents)</h2>
              <p>
                If you are a California resident, the California Consumer Privacy Act (CCPA), as amended by the California Privacy Rights Act (CPRA), provides you with the following rights:
              </p>
              <ul>
                <li><strong>Right to Know:</strong> request disclosure of the categories and specific pieces of personal information we have collected about you, and how it has been used and shared.</li>
                <li><strong>Right to Delete:</strong> request deletion of personal information we have collected from you, subject to certain exceptions.</li>
                <li><strong>Right to Correct:</strong> request correction of inaccurate personal information.</li>
                <li><strong>Right to Opt-Out:</strong> opt out of the &quot;sale&quot; or &quot;sharing&quot; of your personal information, including for cross-context behavioral advertising.</li>
                <li><strong>Right to Limit Use of Sensitive Personal Information,</strong> where applicable.</li>
                <li><strong>Right to Non-Discrimination:</strong> we will not discriminate against you for exercising any of these rights.</li>
              </ul>
              <p>
                To exercise these rights, contact us using the details in Section 11. We may need to verify your identity before fulfilling your request. You may also designate an authorized agent to submit a request on your behalf.
              </p>

              <h2>7. Other US State Privacy Rights</h2>
              <p>
                Depending on your state of residence, you may have similar rights under other US state privacy laws (such as Virginia, Colorado, Connecticut, or other applicable state statutes), including rights to access, delete, correct, and opt out of certain processing. We will honor applicable requests consistent with those laws.
              </p>

              <h2>8. Children&apos;s Privacy</h2>
              <p>
                The Site and Services are not directed to individuals under the age of 18, and we do not knowingly collect personal information from children. If you believe we have inadvertently collected such information, please contact us so we can delete it.
              </p>

              <h2>9. Third-Party Links</h2>
              <p>
                The Site may contain links to third-party websites or services. This Privacy Policy does not apply to those third parties, and we encourage you to review their privacy policies separately.
              </p>

              <h2>10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated &quot;Effective Date.&quot; Your continued use of the Site or Services after changes are posted constitutes acceptance of the revised policy.
              </p>

              <h2>30-Day Refund Policy</h2>
              <p>
                We offer a <strong>30-day refund policy</strong>. If you are not satisfied with your purchase, you may request a refund within <strong>30 days</strong> of the original purchase date, subject to our refund terms and eligibility requirements.
              </p>

              <h2>11. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us at:
              </p>
              <p>
                Acsytech<br />
                Email: support@acsytech.com<br />
                Address: Box 491, 4 E Central St, Worcester, MA 01613
              </p>
            </Div>
          </Div>
        </Div>
      </Div>
      <Spacing lg="100" md="60" />
    </>
  );
}
